<template>
  <div class="dashboard-container">
    <!-- HERO -->
    <header class="dash-hero">
      <div class="dash-hero-grid">
        <div class="dash-hero-main">
          <div class="dash-hero-title">SALES REPORT</div>
        </div>
      </div>
    </header>

    <!-- FILTERS -->
    <section class="dash-card dash-filtercard">
      <div class="dash-filters-row">
        <div class="dash-fb-item dash-fb-dates">
          <div class="dash-inputgroup">
            <span class="dash-inputgroup-prepend">
              <b-icon-calendar-range></b-icon-calendar-range>
            </span>
            <date-picker
              v-model="date_range"
              format="MM-DD-YYYY"
              valueType="format"
              class="dash-date-input"
              placeholder="Select Date Range"
              range
            />
          </div>
        </div>

        <div class="dash-quickdates">
          <button
            v-for="btn in quickDateButtons"
            :key="btn.id"
            class="dash-quick-btn"
            :class="{ 'is-active': picked_date_range === btn.id }"
            @click="set_dates(btn.id)"
          >{{ btn.label }}</button>
        </div>

        <button
          class="dash-btn-primary dash-search-btn"
          @click="fetchSummaryData"
          :disabled="isLoading"
        >
          <b-icon :icon="isLoading ? 'arrow-repeat' : 'search'" :animation="isLoading ? 'spin' : null"></b-icon>
          {{ isLoading ? 'Loading…' : 'Analyze' }}
        </button>
      </div>

      <div class="dash-advanced-toggle-wrap">
        <button class="dash-linkbtn" @click="showAdvancedFilters = !showAdvancedFilters">
          <b-icon :icon="showAdvancedFilters ? 'chevron-up' : 'chevron-down'"></b-icon>
          {{ showAdvancedFilters ? 'Hide' : 'Show' }} advanced filters
        </button>
      </div>

      <b-collapse v-model="showAdvancedFilters">
        <div class="dash-advanced-grid">
          <div class="dash-adv-item">
            <small class="dash-label"><b-icon-geo-alt></b-icon-geo-alt> LOCATION</small>
            <b-form-select :options="locationOptions" v-model="selectedLocation"
                           class="dash-select" @change="onLocationChange">
              <template #first>
                <b-form-select-option :value="null">All Locations</b-form-select-option>
              </template>
            </b-form-select>
          </div>

          <div class="dash-adv-item">
            <small class="dash-label"><b-icon-person></b-icon-person> SALESPERSON</small>
            <b-form-select :options="salespersonOptions" v-model="selectedSalesperson"
                           class="dash-select" @change="onSalespersonChange">
              <template #first>
                <b-form-select-option :value="null">All Salespeople</b-form-select-option>
              </template>
            </b-form-select>
          </div>

          <div class="dash-adv-item">
            <small class="dash-label"><b-icon-tag></b-icon-tag> SALE TYPE</small>
            <b-form-select :options="saleTypeOptions" v-model="selectedSaleType"
                           class="dash-select" @change="onFilterChange"></b-form-select>
          </div>

          <div class="dash-adv-item">
            <small class="dash-label"><b-icon-table></b-icon-table> VIEW MODE</small>
            <b-form-radio-group
              v-model="viewMode"
              buttons
              button-variant="outline-secondary"
              size="md"
              class="dash-radio-group"
              @change="onViewModeChange"
            >
              <b-form-radio value="summary">Summary View</b-form-radio>
              <b-form-radio value="detailed">Detailed View</b-form-radio>
            </b-form-radio-group>
          </div>
        </div>
      </b-collapse>
    </section>

    <!-- LOADING -->
    <transition name="dash-fade">
      <div v-if="isLoading" class="dash-card dash-loading-inline">
        <b-spinner variant="primary"></b-spinner>
        <span>Fetching sales data…</span>
      </div>
    </transition>

    <!-- MAIN -->
    <template v-if="!isLoading && summaryData && summaryData.total_sales > 0">
      <!-- KPI CARDS -->
      <section class="dash-kpi-grid">
        <div class="dash-kpi is-total">
          <div class="dash-kpi-icon"><b-icon-cash-stack></b-icon-cash-stack></div>
          <div class="dash-kpi-body">
            <div class="dash-kpi-value">{{ formatCurrency(summaryData.total_sales) }}</div>
            <div class="dash-kpi-label">Total Sales</div>
            <div class="dash-kpi-trend">{{ summaryData.total_orders }} orders</div>
          </div>
        </div>

        <div class="dash-kpi is-regular">
          <div class="dash-kpi-icon"><b-icon-cart-plus></b-icon-cart-plus></div>
          <div class="dash-kpi-body">
            <div class="dash-kpi-value">{{ formatCurrency(summaryData.regular_sales) }}</div>
            <div class="dash-kpi-label">Regular Sales</div>
            <div class="dash-kpi-trend">
              {{ ((summaryData.regular_sales / summaryData.total_sales) * 100).toFixed(1) }}% of total
            </div>
          </div>
        </div>

        <div class="dash-kpi is-net">
          <div class="dash-kpi-icon"><b-icon-wallet></b-icon-wallet></div>
          <div class="dash-kpi-body">
            <div class="dash-kpi-value">{{ formatCurrency(summaryData.total_net_sales) }}</div>
            <div class="dash-kpi-label">Net Sales</div>
            <div class="dash-kpi-trend">Regular + Layaway − Refunds</div>
          </div>
        </div>

        <div class="dash-kpi is-layaway">
          <div class="dash-kpi-icon"><b-icon-clock-history></b-icon-clock-history></div>
          <div class="dash-kpi-body">
            <div class="dash-kpi-value">{{ formatCurrency(summaryData.layaway_sales) }}</div>
            <div class="dash-kpi-label">Written Layaway</div>
            <div class="dash-kpi-trend">Layaway Paid: {{ formatCurrency(summaryData.layaway_paid) }}</div>
          </div>
        </div>

        <div class="dash-kpi is-refund">
          <div class="dash-kpi-icon"><b-icon-arrow-return-left></b-icon-arrow-return-left></div>
          <div class="dash-kpi-body">
            <div class="dash-kpi-value">{{ formatCurrency(summaryData.total_refund) }}</div>
            <div class="dash-kpi-label">Refunds</div>
            <div class="dash-kpi-trend" :class="refundRate < 5 ? 'is-good' : 'is-bad'">
              {{ refundRate }}% of total
            </div>
          </div>
        </div>

        <div v-if="isAdmin" class="dash-kpi is-commission">
          <div class="dash-kpi-icon"><b-icon-graph-up></b-icon-graph-up></div>
          <div class="dash-kpi-body">
            <div class="dash-kpi-value">{{ formatCurrency(summaryData.total_commission) }}</div>
            <div class="dash-kpi-label">Commission</div>
            <div class="dash-kpi-trend">{{ commissionRate }}% rate</div>
          </div>
        </div>
      </section>

      <!-- CHARTS -->
      <div class="dash-charts-grid">
        <section class="dash-card dash-chart-card">
          <div class="dash-card-header">
            <div class="dash-card-title">
              <b-icon-bar-chart-line></b-icon-bar-chart-line>
              {{ chartTitle }}
            </div>
            <div class="dash-chart-legend" v-if="chartMode === 'week'">
              <span class="dash-legend-item">
                <span class="dash-legend-swatch is-solid"></span> Daily
              </span>
              <span class="dash-legend-item">
                <span class="dash-legend-swatch is-dashed"></span> Hourly
              </span>
            </div>
          </div>
          <div class="dash-chart-body">
            <canvas ref="dailyTrendChart"></canvas>
          </div>
        </section>

        <section class="dash-card dash-chart-card">
          <div class="dash-card-header">
            <div class="dash-card-title">
              <b-icon-pie-chart></b-icon-pie-chart>
              Sales by Location
            </div>
          </div>
          <div class="dash-chart-body">
            <canvas ref="locationChart"></canvas>
          </div>
        </section>

        <!-- Time on LEFT, days of the week on BOTTOM (any 7-day range) -->
        <section v-if="weekdayColumns.length" class="dash-card dash-chart-card dash-chart-card--wide">
          <button
            type="button"
            class="dash-card-header dash-collapse-header"
            :class="{ 'is-open': showWeekdayGrid }"
            :aria-expanded="showWeekdayGrid ? 'true' : 'false'"
            @click="showWeekdayGrid = !showWeekdayGrid"
          >
            <span class="dash-card-title">
              <b-icon-calendar-week></b-icon-calendar-week>
              Sales &amp; Refunds by Day and Hour
            </span>
            <span class="dash-collapse-action">
              {{ showWeekdayGrid ? 'Hide' : 'Show' }}
              <b-icon icon="chevron-down" class="dash-collapse-chevron"></b-icon>
            </span>
          </button>
          <transition name="dash-fade">
            <hourly-bar-chart
              v-if="showWeekdayGrid"
              :columns="weekdayColumns"
              :sales-data="weekdaySalesData"
              :column-key="weekdayKey"
              nav
              :nav-label="gridWeekLabel"
              :can-next="gridWeekCanNext"
              :show-reset="gridWeekOffset !== 0"
              :loading="gridWeekLoading"
              @prev="shiftGridWeek(-1)"
              @next="shiftGridWeek(1)"
              @reset="shiftGridWeek(-gridWeekOffset)"
            />
          </transition>
        </section>

        <!-- Time on LEFT, stores on BOTTOM (any date range) -->
        <section class="dash-card dash-chart-card dash-chart-card--wide">
          <button
            type="button"
            class="dash-card-header dash-collapse-header"
            :class="{ 'is-open': showStoreGrid }"
            :aria-expanded="showStoreGrid ? 'true' : 'false'"
            @click="showStoreGrid = !showStoreGrid"
          >
            <span class="dash-card-title">
              <b-icon-shop-window></b-icon-shop-window>
              Store Sales &amp; Refunds by Hour
            </span>
            <span class="dash-collapse-action">
              {{ showStoreGrid ? 'Hide' : 'Show' }}
              <b-icon icon="chevron-down" class="dash-collapse-chevron"></b-icon>
            </span>
          </button>
          <transition name="dash-fade">
            <hourly-bar-chart
              v-if="showStoreGrid"
              :columns="storeColumns"
              :sales-data="rawSalesData"
              :column-key="storeKey"
            />
          </transition>
        </section>
      </div>

      <!-- Filter notice -->
      <div v-if="selectedLocation || selectedSalesperson" class="dash-noticebar">
        <span class="dash-chip is-orange">
          <b-icon-info-circle></b-icon-info-circle>
          Showing
          <strong v-if="selectedLocation && selectedSalesperson">
            {{ getLocationName(selectedLocation) }} — {{ selectedSalesperson }}
          </strong>
          <strong v-else-if="selectedLocation">{{ getLocationName(selectedLocation) }}</strong>
          <strong v-else-if="selectedSalesperson">{{ selectedSalesperson }}</strong>
        </span>
        <button class="dash-linkbtn" @click="clearFilters">
          <b-icon-x></b-icon-x> Clear Filters
        </button>
      </div>

      <!-- Toggle #2 (above tables) -->
      <div class="dash-viewmode-row">
        <b-form-radio-group
          v-model="viewMode"
          buttons
          button-variant="outline-secondary"
          size="md"
          class="dash-radio-group"
          @change="onViewModeChange"
        >
          <b-form-radio value="summary">Summary View</b-form-radio>
          <b-form-radio value="detailed">Detailed View</b-form-radio>
        </b-form-radio-group>
      </div>

      <!-- SUMMARY TABLES -->
      <div class="dash-tables-grid">
        <!-- Location -->
        <section class="dash-card dash-tablecard">
          <div class="dash-card-header">
            <div class="dash-card-title">
              <b-icon-building></b-icon-building>
              Performance by Location
              <span class="dash-table-badge">
                <b-icon-mouse2></b-icon-mouse2> Click row for details
              </span>
            </div>
          </div>
          <div class="dash-table-scroll">
            <table class="dash-table">
              <thead>
                <tr>
                  <th>Location</th>
                  <th class="is-right">Regular</th>
                  <th class="is-right">Layaway</th>
                  <th class="is-right">Layaway Paid</th>
                  <th class="is-right">Refunds</th>
                  <th class="is-right">Net</th>
                  <th class="is-right">Orders</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(data, locationId) in locationSummary"
                  :key="locationId"
                  class="dash-row is-clickable"
                  :class="{ 'is-highlight': selectedLocation == locationId }"
                  @click="selectLocationFromTable(locationId)"
                >
                  <td class="dash-loc-cell">
                    <b-icon-geo-alt-fill class="dash-loc-icon"></b-icon-geo-alt-fill>
                    <span class="dash-cell-name">{{ getLocationName(locationId) }}</span>
                  </td>
                  <td class="is-right dash-num">{{ formatCurrency(data.regular_sales || 0) }}</td>
                  <td class="is-right dash-num">{{ formatCurrency(data.layaway_total || 0) }}</td>
                  <td class="is-right dash-num">{{ formatCurrency(data.layaway_paid || 0) }}</td>
                  <td class="is-right dash-num is-danger">{{ formatCurrency(data.refund || 0) }}</td>
                  <td class="is-right dash-num is-teal">{{ formatCurrency(data.net || 0) }}</td>
                  <td class="is-right dash-num">{{ data.count || 0 }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="dash-totals-row">
                  <td><span class="dash-totals-label">TOTAL</span></td>
                  <td class="is-right dash-totals-amt">{{ formatCurrency(totalLocationRegularSales) }}</td>
                  <td class="is-right dash-totals-amt">{{ formatCurrency(totalLocationLayawayTotal) }}</td>
                  <td class="is-right dash-totals-amt">{{ formatCurrency(totalLocationLayawayPaid) }}</td>
                  <td class="is-right dash-totals-amt">{{ formatCurrency(totalLocationRefunds) }}</td>
                  <td class="is-right dash-totals-amt">{{ formatCurrency(totalLocationNet) }}</td>
                  <td class="is-right dash-totals-amt">{{ totalLocationOrders }}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </section>

        <!-- Salesperson — fixed layout, no scroll -->
        <section class="dash-card dash-tablecard">
          <div class="dash-card-header">
            <div class="dash-card-title">
              <b-icon-people></b-icon-people>
              Performance by Salesperson
              <span class="dash-table-badge">
                <b-icon-mouse2></b-icon-mouse2> Click row for details
              </span>
            </div>
          </div>
          <div class="dash-table-scroll dash-table-scroll--fixed">
            <table class="dash-table dash-table--sp">
              <colgroup>
                <col style="width: 26%;" />
                <col style="width: 11%;" />
                <col style="width: 11%;" />
                <col style="width: 12%;" />
                <col style="width: 10%;" />
                <col style="width: 12%;" />
                <col style="width: 7%;"  />
                <col style="width: 11%;" />
              </colgroup>
              <thead>
                <tr>
                  <th>Salesperson</th>
                  <th class="is-right">Regular</th>
                  <th class="is-right">Layaway</th>
                  <th class="is-right">Layaway Paid</th>
                  <th class="is-right">Refunds</th>
                  <th class="is-right">Net</th>
                  <th class="is-right">Orders</th>
                  <th class="is-right">Avg Order</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(data, salesperson) in salespersonSummary"
                  :key="salesperson"
                  class="dash-row is-clickable"
                  :class="{ 'is-highlight': selectedSalesperson == salesperson }"
                  @click="selectSalespersonFromTable(salesperson)"
                >
                  <td class="dash-loc-cell">
                    <b-icon-person-fill class="dash-loc-icon"></b-icon-person-fill>
                    <span class="dash-sp-name">{{ salesperson }}</span>
                  </td>
                  <td class="is-right dash-num">{{ formatCurrency(data.regular_sales || 0) }}</td>
                  <td class="is-right dash-num">{{ formatCurrency(data.layaway_total || 0) }}</td>
                  <td class="is-right dash-num">{{ formatCurrency(data.layaway_paid || 0) }}</td>
                  <td class="is-right dash-num is-danger">{{ formatCurrency(data.refund || 0) }}</td>
                  <td class="is-right dash-num is-teal">{{ formatCurrency(data.net || 0) }}</td>
                  <td class="is-right dash-num">{{ data.count || 0 }}</td>
                  <td class="is-right dash-num">{{ formatCurrency((data.net || 0) / (data.count || 1)) }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="dash-totals-row">
                  <td><span class="dash-totals-label">TOTAL</span></td>
                  <td class="is-right dash-totals-amt">{{ formatCurrency(totalSalespersonRegularSales) }}</td>
                  <td class="is-right dash-totals-amt">{{ formatCurrency(totalSalespersonLayawayTotal) }}</td>
                  <td class="is-right dash-totals-amt">{{ formatCurrency(totalSalespersonLayawayPaid) }}</td>
                  <td class="is-right dash-totals-amt">{{ formatCurrency(totalSalespersonRefunds) }}</td>
                  <td class="is-right dash-totals-amt">{{ formatCurrency(totalSalespersonNet) }}</td>
                  <td class="is-right dash-totals-amt">{{ totalSalespersonOrders }}</td>
                  <td class="is-right dash-totals-amt">{{ formatCurrency(totalSalespersonAvgOrder) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </section>
      </div>

      <!-- DETAILED -->
      <div v-if="isLoadingDetailed" class="dash-card dash-loading-inline">
        <b-spinner variant="primary"></b-spinner>
        <span>Loading detailed transactions…</span>
      </div>

      <section v-if="viewMode === 'detailed'" class="dash-card dash-detailcard">
        <div class="dash-card-header">
          <div class="dash-card-title">
            <b-icon-table></b-icon-table>
            Detailed Transactions
            <span v-if="selectedLocation && selectedSalesperson"> — {{ getLocationName(selectedLocation) }} · {{ selectedSalesperson }}</span>
            <span v-else-if="selectedLocation"> — {{ getLocationName(selectedLocation) }}</span>
            <span v-else-if="selectedSalesperson"> — {{ selectedSalesperson }}</span>
            <span class="dash-table-badge">{{ filteredDetailedData.length }} records</span>
          </div>

          <div class="dash-detail-actions">
            <b-dropdown :variant="selectedOrderTypes.length > 0 ? 'info' : 'outline-secondary'"
                        size="sm" right no-flip class="dash-dd">
              <template #button-content>
                <b-icon-truck></b-icon-truck> Order Type
                <b-badge v-if="selectedOrderTypes.length > 0" variant="dark" class="ml-1">{{ selectedOrderTypes.length }}</b-badge>
              </template>
              <b-dropdown-form @click.stop>
                <b-form-checkbox v-for="opt in orderTypeOptions" :key="opt.value"
                                 v-model="selectedOrderTypes" :value="opt.value" class="mb-2">{{ opt.text }}</b-form-checkbox>
                <b-button v-if="selectedOrderTypes.length > 0" size="sm" variant="outline-secondary"
                          block class="mt-2" @click="selectedOrderTypes = []">Clear</b-button>
              </b-dropdown-form>
            </b-dropdown>

            <b-dropdown :variant="selectedItemFlags.length > 0 ? 'warning' : 'outline-secondary'"
                        size="sm" right no-flip class="dash-dd">
              <template #button-content>
                <b-icon-tag-fill></b-icon-tag-fill> Discount / As-Is
                <b-badge v-if="selectedItemFlags.length > 0" variant="dark" class="ml-1">{{ selectedItemFlags.length }}</b-badge>
              </template>
              <b-dropdown-form @click.stop>
                <b-form-checkbox v-for="opt in itemFlagOptions" :key="opt.value"
                                 v-model="selectedItemFlags" :value="opt.value" class="mb-2">{{ opt.text }}</b-form-checkbox>
                <b-button v-if="selectedItemFlags.length > 0" size="sm" variant="outline-secondary"
                          block class="mt-2" @click="selectedItemFlags = []">Clear</b-button>
              </b-dropdown-form>
            </b-dropdown>

            <b-dropdown :variant="selectedDeliveryStatuses.length > 0 ? 'success' : 'outline-secondary'"
                        size="sm" right no-flip class="dash-dd">
              <template #button-content>
                <b-icon-check-circle></b-icon-check-circle> Status
                <b-badge v-if="selectedDeliveryStatuses.length > 0" variant="dark" class="ml-1">{{ selectedDeliveryStatuses.length }}</b-badge>
              </template>
              <b-dropdown-form @click.stop>
                <b-form-checkbox v-for="opt in deliveryStatusOptions" :key="opt.value"
                                 v-model="selectedDeliveryStatuses" :value="opt.value" class="mb-2">{{ opt.text }}</b-form-checkbox>
                <b-button v-if="selectedDeliveryStatuses.length > 0" size="sm" variant="outline-secondary"
                          block class="mt-2" @click="selectedDeliveryStatuses = []">Clear</b-button>
              </b-dropdown-form>
            </b-dropdown>

            <button class="dash-quick-btn" :class="{ 'is-active': showWarrantyWithDiscount }"
                    @click="showWarrantyWithDiscount = !showWarrantyWithDiscount">
              <b-icon-shield-check></b-icon-shield-check> Warranty + Discount
              <span v-if="showWarrantyWithDiscount" class="dash-mini-badge">{{ filteredDetailedData.length }}</span>
            </button>

            <button class="dash-btn-ghost" @click="exportToCSV">
              <b-icon-download></b-icon-download> Export
            </button>
            <button class="dash-btn-ghost" @click="refreshDetailedData">
              <b-icon-arrow-repeat></b-icon-arrow-repeat> Refresh
            </button>
          </div>
        </div>

        <div class="compact-detail-wrapper">
          <table class="sales-table compact-sales-table">
            <thead>
              <tr style="background: #c3cdd3;">
                <th class="col-date"     @click="sortDetailedBy('ts')">DATE</th>
                <th class="col-loc"      @click="sortDetailedBy('location_id')">SOLD FROM</th>
                <th class="col-inv"      @click="sortDetailedBy('invoice_id')">INV #</th>
                <th class="col-by"       @click="sortDetailedBy('username')">SOLD BY</th>
                <th style="background-color: lightgray;" class="col-ordertype" @click="sortDetailedBy('order_type')">ORDER TYPE</th>
                <th style="background-color: lightgray;" class="col-status"    @click="sortDetailedBy('delivery_status')">STATUS</th>
                <th style="background-color: lightblue;" class="col-num"    @click="sortDetailedBy('order_total')">ORDER TOTAL</th>
                <th style="background-color: lightblue;" class="col-num-sm" @click="sortDetailedBy('total_discount')">DISC</th>
                <th style="background-color: lightblue;" class="col-num-sm" @click="sortDetailedBy('discount_percentage')">DISC %</th>
                <th style="background-color: lightblue;" class="col-num"    @click="sortDetailedBy('adjusted_base_commission')">S COMM</th>
                <th style="background-color: lightblue;" class="col-num"    @click="sortDetailedBy('extra_charges')">EX CHARGES</th>
                <th style="background-color: lightblue;" class="col-num-sm" @click="sortDetailedBy('sales_tax')">SLS TX</th>
                <th style="background-color: lightblue;" class="col-num"    @click="sortDetailedBy('total_dollar_amount')">INVOICE TOTAL</th>
                <th style="background-color: orange;" class="col-num-sm"    @click="sortDetailedBy('delivery_charges')">DLVRY</th>
                <th style="background-color: orange;" class="col-num-sm"    @click="sortDetailedBy('adjusted_delivery_commission')">D COMM</th>
                <th style="background-color: lightgreen;" class="col-num-sm" @click="sortDetailedBy('setup_charges')">SETUP</th>
                <th style="background-color: lightgreen;" class="col-num-sm" @click="sortDetailedBy('adjusted_setup_commission')">S COMM</th>
                <th style="background-color: yellow;" class="col-num-sm"    @click="sortDetailedBy('warranty_charges')">WRNTY</th>
                <th style="background-color: yellow;" class="col-num-sm"    @click="sortDetailedBy('adjusted_warranty_commission')">W COMM</th>
                <th style="background-color: lightpink;" class="col-num-sm" @click="sortDetailedBy('spiff_commission')">SPIFF</th>
                <th style="background-color: lightpink;" class="col-num-sm" @click="sortDetailedBy('adjusted_total_commission')">T COMM</th>
              </tr>
            </thead>
            <tbody>
              <!-- Totals Row -->
              <tr style="background: lightyellow;">
                <td class="header" colspan="6" style="text-align: right;"><strong>TOTALS:</strong></td>
                <td class="header"><strong>${{ totalDetailedOrderTotal.toFixed(2) }}</strong></td>
                <td class="header">${{ totalDetailedDiscount.toFixed(2) }}</td>
                <td class="header">{{ totalDetailedDiscountPercent.toFixed(2) }}%</td>
                <td class="header"><strong>${{ totalDetailedBaseCommission.toFixed(2) }}</strong></td>
                <td class="header">${{ totalDetailedExtraCharges.toFixed(2) }}</td>
                <td class="header">${{ totalDetailedTax.toFixed(2) }}</td>
                <td class="header"><strong>${{ totalDetailedSales.toFixed(2) }}</strong></td>
                <td class="header">${{ totalDetailedDelivery.toFixed(2) }}</td>
                <td class="header">${{ totalDetailedDeliveryComm.toFixed(2) }}</td>
                <td class="header">${{ totalDetailedSetup.toFixed(2) }}</td>
                <td class="header">${{ totalDetailedSetupComm.toFixed(2) }}</td>
                <td class="header">${{ totalDetailedWarranty.toFixed(2) }}</td>
                <td class="header">${{ totalDetailedWarrantyComm.toFixed(2) }}</td>
                <td class="header">${{ totalDetailedSpiff.toFixed(2) }}</td>
                <td class="header"><strong>${{ totalDetailedTotalCommission.toFixed(2) }}</strong></td>
              </tr>

              <!-- Data Rows -->
              <tr v-for="(item, idx) in paginatedDetailedData" :key="idx"
                  :style="{ background: getRowBackgroundColor(item), fontWeight: (item.discount_percentage || 0) > 5 ? 'bold' : 'normal' }">
                <td class="non-header">{{ formatDateTime(item.ts) }}</td>
                <td class="non-header">{{ getLocationName(item.location_id) }}</td>
                <td class="non-header">
                  <a href="#" @click.prevent="openInvoice(item.invoice_id)" class="invoice-link"
                     :title="'Open invoice ' + item.invoice_id">
                    {{ item.invoice_id }}
                  </a>
                </td>
                <td class="non-header">{{ item.username }}</td>
                <td class="non-header">
                  {{ item.order_type || '-' }}
                  <b-badge v-if="item.has_discount" variant="warning" class="ml-1 flag-badge" title="Has discounted item">D</b-badge>
                  <b-badge v-if="item.has_as_is" variant="danger" class="ml-1 flag-badge" title="Has as-is item">A</b-badge>
                </td>
                <td class="non-header">
                  <b-badge
                    v-if="item.delivery_status"
                    :variant="item.delivery_status === 'delivered' ? 'success' : 'secondary'"
                    class="status-badge"
                  >{{ item.delivery_status.toUpperCase() }}</b-badge>
                  <span v-else>-</span>
                </td>
                <td class="non-header text-right">${{ (item.order_total || 0).toFixed(2) }}</td>
                <td class="non-header text-right">${{ (item.total_discount || 0).toFixed(2) }}</td>
                <td class="non-header text-right" :style="{ backgroundColor: (item.discount_percentage || 0) > 5 ? '#ffcccc' : 'transparent', fontWeight: 'bold' }">
                  {{ (item.discount_percentage || 0).toFixed(2) }}%
                </td>
                <td class="non-header text-right">${{ (item.adjusted_base_commission || 0).toFixed(2) }}</td>
                <td class="non-header text-right">${{ (item.extra_charges || 0).toFixed(2) }}</td>
                <td class="non-header text-right">${{ (item.sales_tax || 0).toFixed(2) }}</td>
                <td class="non-header text-right" :class="{ 'text-danger': item.total_dollar_amount < 0 }">
                  ${{ (item.total_dollar_amount || 0).toFixed(2) }}
                </td>
                <td class="non-header text-right">${{ (item.delivery_charges || 0).toFixed(2) }}</td>
                <td class="non-header text-right">${{ (item.adjusted_delivery_commission || 0).toFixed(2) }}</td>
                <td class="non-header text-right">${{ (item.setup_charges || 0).toFixed(2) }}</td>
                <td class="non-header text-right">${{ (item.adjusted_setup_commission || 0).toFixed(2) }}</td>
                <td class="non-header text-right">${{ (item.warranty_charges || 0).toFixed(2) }}</td>
                <td class="non-header text-right">${{ (item.adjusted_warranty_commission || 0).toFixed(2) }}</td>
                <td class="non-header text-right">${{ (item.spiff_commission || 0).toFixed(2) }}</td>
                <td class="non-header text-right">
                  <strong>${{ (item.adjusted_total_commission || 0).toFixed(2) }}</strong>
                </td>
              </tr>
              <tr v-if="filteredDetailedData.length === 0 && !isLoadingDetailed">
                <td colspan="21" class="text-center py-4 text-muted" style="font-size: 18px;">
                  No transactions match the selected filters
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="filteredDetailedData.length > 0" class="pagination-wrapper d-flex justify-content-between align-items-center p-3 bg-light">
          <small class="text-muted" style="font-size: 16px;">
            Showing {{ (detailedPage - 1) * detailedPageSize + 1 }} to {{ Math.min(detailedPage * detailedPageSize, filteredDetailedData.length) }} of {{ filteredDetailedData.length }}
          </small>
          <b-pagination
            v-model="detailedPage"
            :total-rows="filteredDetailedData.length"
            :per-page="detailedPageSize"
            size="md"
            class="mb-0"
            align="center"
          ></b-pagination>
          <b-form-select v-model="detailedPageSize" size="md" style="width: 85px;" @change="detailedPage = 1">
            <option :value="10">10</option>
            <option :value="25">25</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </b-form-select>
        </div>
      </section>

      <!-- Hints -->
      <div v-if="viewMode === 'summary' && !(selectedLocation || selectedSalesperson)" class="dash-hint">
        <b-icon-eye-slash></b-icon-eye-slash>
        Summary View: click any row in the tables above to drill down into detailed transactions.
      </div>
      <div v-if="viewMode === 'summary' && (selectedLocation || selectedSalesperson)" class="dash-hint is-info">
        <b-icon-info-circle></b-icon-info-circle>
        Summary View: switch to Detailed View above to see transaction-level data.
      </div>
      <div v-if="viewMode === 'detailed' && !(selectedLocation || selectedSalesperson)" class="dash-hint">
        <b-icon-filter-circle></b-icon-filter-circle>
        Detailed View: select a location or salesperson above to view detailed transactions.
      </div>
    </template>

    <!-- NO DATA -->
    <div v-else-if="!isLoading && !summaryData" class="dash-card dash-empty">
      <div class="dash-empty-icon"><b-icon-search font-scale="2.4"></b-icon-search></div>
      <h3>Ready when you are</h3>
      <p>Select a date range and click <strong>Analyze</strong> to view sales intelligence.</p>
    </div>
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import moment from 'moment';
import 'moment/locale/en-gb';
import Chart from 'chart.js';
import axios from 'axios';
import HourlyBarChart from './HourlyBarChart';

export default {
  name: "SalesDashboardOptimized",
  components: { DatePicker, HourlyBarChart },
  data() {
    return {
      packet: { userDetails: {}, locations: [] },

      isLoading: false,
      isLoadingDetailed: false,
      showAdvancedFilters: false,
      viewMode: 'summary',

      showWarrantyWithDiscount: false,

      selectedOrderTypes: [],
      orderTypeOptions: [
        { value: 'home_delivery',  text: 'Home Delivery'  },
        { value: 'store_pickup',   text: 'Store Pickup'   },
        { value: 'customer_takes', text: 'Customer Takes' }
      ],

      selectedItemFlags: [],
      itemFlagOptions: [
        { value: 'discounted', text: 'Discounted Item' },
        { value: 'as_is',      text: 'As-Is Item' }
      ],

      selectedDeliveryStatuses: [],
      deliveryStatusOptions: [
        { value: 'delivered', text: 'Delivered (state = 1)' },
        { value: 'written',   text: 'Written (other states)' }
      ],

      summaryData: null,
      locationSummary: {},
      salespersonSummary: {},
      dailyTrend: {},
      rawSalesData: [],

      detailedData: [],

      date_range: [],
      picked_date_range: null,
      selectedLocation: null,
      selectedSalesperson: null,
      selectedSaleType: null,

      detailedSortKey: 'ts',
      detailedSortAsc: false,
      detailedPage: 1,
      detailedPageSize: 25,

      quickDateButtons: [
        { id: 'today',       label: 'Today' },
        { id: 'yesterday',   label: 'Yesterday' },
        { id: 'this_week',   label: 'This Week' },
        { id: 'last_week',   label: 'Last Week' },
        { id: 'this_month',  label: 'This Month' },
        { id: 'last_month',  label: 'Last Month' }
      ],

      saleTypeOptions: [
        { value: null, text: 'All Types' },
        { value: 'regular', text: 'Regular' },
        { value: 'layaway', text: 'Layaway' },
        { value: 'refund', text: 'Refund' }
      ],

      dailyTrendChart: null,
      locationChart: null,
      analyzedRange: [],
      showWeekdayGrid: false,
      showStoreGrid: false,
      gridWeekOffset: 0,
      gridWeekData: [],
      gridWeekLoading: false,
      gridWeekReq: 0,

      chartColors: {
        primary: '#6366f1',
        success: '#10b981',
        warning: '#f59e0b',
        danger: '#ef4444',
        info: '#06b6d4',
        purple: '#8b5cf6'
      }
    };
  },

  computed: {
    isAdmin() { return this.packet.userDetails.userrole === 'admin'; },
    commissionRate() {
      if (!this.summaryData || this.summaryData.total_sales === 0) return 0;
      return ((this.summaryData.total_commission / this.summaryData.total_sales) * 100).toFixed(1);
    },
    refundRate() {
      if (!this.summaryData || this.summaryData.total_sales === 0) return 0;
      return ((this.summaryData.total_refund / this.summaryData.total_sales) * 100).toFixed(1);
    },

    locationOptions() {
      if (!this.packet.locations) return [];
      return this.packet.locations.map(loc => ({ value: loc.value, text: loc.text }));
    },
    salespersonOptions() {
      return Object.keys(this.salespersonSummary).map(name => ({ value: name, text: name }));
    },

    chartMode() {
      if (this.picked_date_range === 'today' || this.picked_date_range === 'yesterday') return 'day';
      if (this.picked_date_range === 'this_week' || this.picked_date_range === 'last_week') return 'week';
      return 'range';
    },
    chartTitle() {
      if (this.chartMode === 'day')  return 'Hourly Sales & Refunds';
      if (this.chartMode === 'week') return 'Daily + Hourly Sales & Refunds';
      return 'Daily Sales & Refunds Trend';
    },

    gridBaseWeekStart() {
      const r = this.analyzedRange;
      if (!r || r.length < 2) return null;
      const start = moment(r[0], 'MM-DD-YYYY');
      const end   = moment(r[1], 'MM-DD-YYYY');
      if (!start.isValid() || !end.isValid() || end.diff(start, 'days') + 1 !== 7) return null;
      return start.format('YYYY-MM-DD');
    },
    gridWeekStart() {
      if (!this.gridBaseWeekStart) return null;
      return moment(this.gridBaseWeekStart, 'YYYY-MM-DD').add(this.gridWeekOffset * 7, 'days');
    },
    weekdayColumns() {
      if (!this.gridWeekStart) return [];
      return Array.from({ length: 7 }, (_, i) => {
        const d = this.gridWeekStart.clone().add(i, 'days');
        return { key: d.format('YYYY-MM-DD'), label: d.format('ddd'), sub: d.format('MMM D') };
      });
    },
    weekdaySalesData() {
      return this.gridWeekOffset === 0 ? this.rawSalesData : this.gridWeekData;
    },
    gridWeekLabel() {
      if (!this.gridWeekStart) return '';
      const s = this.gridWeekStart, e = s.clone().add(6, 'days');
      return s.year() === e.year()
        ? `${s.format('MMM D')} – ${e.format('MMM D, YYYY')}`
        : `${s.format('MMM D, YYYY')} – ${e.format('MMM D, YYYY')}`;
    },
    gridWeekCanNext() {
      if (!this.gridWeekStart) return false;
      return this.gridWeekStart.clone().add(7, 'days').isSameOrBefore(moment(), 'day');
    },
    storeColumns() {
      return Object.keys(this.locationSummary || {}).map(id => ({
        key: String(id), label: this.getLocationName(id), sub: ''
      }));
    },

    totalLocationRegularSales()   { return Object.values(this.locationSummary).reduce((s, l) => s + (l.regular_sales || 0), 0); },
    totalLocationLayawayTotal()   { return Object.values(this.locationSummary).reduce((s, l) => s + (l.layaway_total || 0), 0); },
    totalLocationLayawayPaid()    { return Object.values(this.locationSummary).reduce((s, l) => s + (l.layaway_paid || 0), 0); },
    totalLocationRefunds()        { return Object.values(this.locationSummary).reduce((s, l) => s + (l.refund || 0), 0); },
    totalLocationNet()            { return Object.values(this.locationSummary).reduce((s, l) => s + (l.net || 0), 0); },
    totalLocationOrders()         { return Object.values(this.locationSummary).reduce((s, l) => s + (l.count || 0), 0); },

    totalSalespersonRegularSales() { return Object.values(this.salespersonSummary).reduce((s, p) => s + (p.regular_sales || 0), 0); },
    totalSalespersonLayawayTotal() { return Object.values(this.salespersonSummary).reduce((s, p) => s + (p.layaway_total || 0), 0); },
    totalSalespersonLayawayPaid()  { return Object.values(this.salespersonSummary).reduce((s, p) => s + (p.layaway_paid || 0), 0); },
    totalSalespersonRefunds()      { return Object.values(this.salespersonSummary).reduce((s, p) => s + (p.refund || 0), 0); },
    totalSalespersonNet()          { return Object.values(this.salespersonSummary).reduce((s, p) => s + (p.net || 0), 0); },
    totalSalespersonOrders()       { return Object.values(this.salespersonSummary).reduce((s, p) => s + (p.count || 0), 0); },
    totalSalespersonAvgOrder() {
      const n = this.totalSalespersonOrders;
      return n === 0 ? 0 : this.totalSalespersonNet / n;
    },

    filteredDetailedData() {
      let f = [...this.detailedData];
      if (this.selectedLocation)    f = f.filter(i => i.location_id == this.selectedLocation);
      if (this.selectedSalesperson) f = f.filter(i => i.username === this.selectedSalesperson);
      if (this.selectedSaleType) {
        f = f.filter(i => {
          if (this.selectedSaleType === 'refund') return i.total_dollar_amount < 0;
          if (this.selectedSaleType === 'layaway') return i.sale_type === 'layaway';
          if (this.selectedSaleType === 'regular') return i.sale_type !== 'layaway' && i.total_dollar_amount >= 0;
          return true;
        });
      }
      if (this.showWarrantyWithDiscount) {
        f = f.filter(i => (i.warranty_charges || 0) > 0 && (i.total_discount || 0) > 0);
      }
      if (this.selectedOrderTypes.length) {
        f = f.filter(i => {
          const ot = (i.order_type || '').toLowerCase();
          return this.selectedOrderTypes.some(t => ot.includes(t));
        });
      }
      if (this.selectedItemFlags.length) {
        f = f.filter(i => this.selectedItemFlags.some(flag => {
          if (flag === 'discounted') return i.has_discount === true;
          if (flag === 'as_is')      return i.has_as_is === true;
          return false;
        }));
      }
      if (this.selectedDeliveryStatuses.length) {
        f = f.filter(i => {
          const ds = (i.delivery_status || '').toLowerCase();
          return this.selectedDeliveryStatuses.some(s => ds === s);
        });
      }
      if (this.detailedSortKey) {
        f = f.slice().sort((a, b) => {
          let av = a[this.detailedSortKey] || 0;
          let bv = b[this.detailedSortKey] || 0;
          if (this.detailedSortKey === 'ts') {
            av = moment(a.ts).valueOf();
            bv = moment(b.ts).valueOf();
          }
          if (typeof av === 'string') {
            return this.detailedSortAsc ? av.localeCompare(bv) : bv.localeCompare(av);
          }
          return this.detailedSortAsc ? av - bv : bv - av;
        });
      }
      return f;
    },

    paginatedDetailedData() {
      const start = (this.detailedPage - 1) * this.detailedPageSize;
      return this.filteredDetailedData.slice(start, start + this.detailedPageSize);
    },

    totalDetailedOrderTotal()        { return this.filteredDetailedData.reduce((s, i) => s + (i.order_total || 0), 0); },
    totalDetailedBaseCommission()    { return this.filteredDetailedData.reduce((s, i) => s + (i.adjusted_base_commission || 0), 0); },
    totalDetailedExtraCharges()      { return this.filteredDetailedData.reduce((s, i) => s + (i.extra_charges || 0), 0); },
    totalDetailedSales()             { return this.filteredDetailedData.reduce((s, i) => s + (i.total_dollar_amount || 0), 0); },
    totalDetailedTax()               { return this.filteredDetailedData.reduce((s, i) => s + (i.sales_tax || 0), 0); },
    totalDetailedDiscount()          { return this.filteredDetailedData.reduce((s, i) => s + (i.total_discount || 0), 0); },
    totalDetailedWarranty()          { return this.filteredDetailedData.reduce((s, i) => s + (i.warranty_charges || 0), 0); },
    totalDetailedWarrantyComm()      { return this.filteredDetailedData.reduce((s, i) => s + (i.adjusted_warranty_commission || 0), 0); },
    totalDetailedSetup()             { return this.filteredDetailedData.reduce((s, i) => s + (i.setup_charges || 0), 0); },
    totalDetailedSetupComm()         { return this.filteredDetailedData.reduce((s, i) => s + (i.adjusted_setup_commission || 0), 0); },
    totalDetailedDelivery()          { return this.filteredDetailedData.reduce((s, i) => s + (i.delivery_charges || 0), 0); },
    totalDetailedDeliveryComm()      { return this.filteredDetailedData.reduce((s, i) => s + (i.adjusted_delivery_commission || 0), 0); },
    totalDetailedSpiff()             { return this.filteredDetailedData.reduce((s, i) => s + (i.spiff_commission || 0), 0); },
    totalDetailedTotalCommission()   { return this.filteredDetailedData.reduce((s, i) => s + (i.adjusted_total_commission || 0), 0); },
    totalDetailedDiscountPercent() {
      const sold = this.totalDetailedOrderTotal;
      const disc = this.totalDetailedDiscount;
      const orig = sold + disc;
      return orig <= 0 ? 0 : (disc / orig) * 100;
    }
  },

  watch: {
    selectedLocation() {
      this.detailedPage = 1;
      if (this.viewMode === 'detailed' && (this.selectedLocation || this.selectedSalesperson)) this.fetchDetailedData();
    },
    selectedSalesperson() {
      this.detailedPage = 1;
      if (this.viewMode === 'detailed' && (this.selectedLocation || this.selectedSalesperson)) this.fetchDetailedData();
    },
    selectedSaleType()         { this.detailedPage = 1; },
    showWarrantyWithDiscount() { this.detailedPage = 1; },
    selectedOrderTypes()       { this.detailedPage = 1; },
    selectedItemFlags()        { this.detailedPage = 1; },
    selectedDeliveryStatuses() { this.detailedPage = 1; }
  },

  mounted() {
    this.initializeDashboard();
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    this.destroyCharts();
  },
  created() {
    this.loadUserData();
  },

  methods: {
    formatCurrency(v) {
      if (v === undefined || v === null) return '$0.00';
      const n = parseFloat(v);
      if (isNaN(n)) return '$0.00';
      return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    },

    formatDateTime(ts) {
      return ts ? moment(ts).format('MM-DD-YYYY HH:mm:ss A') : '-';
    },

    getLocationName(locationId) {
      if (!this.packet.locations) return `Location ${locationId}`;
      const l = this.packet.locations.find(x => x.value == locationId);
      return l ? l.text : `Location ${locationId}`;
    },

    openInvoice(invoiceId) {
      if (!invoiceId) return;
      const routeData = this.$router.resolve({ path: `/sales/viewsale/${invoiceId}/` });
      window.open(routeData.href, '_blank', 'noopener,noreferrer');
    },

    getRowBackgroundColor(item) {
      if (item.total_dollar_amount < 0) return '#ffab91';
      if ((item.discount_percentage || 0) > 5) return '#ffcccc';
      return 'white';
    },

    sortDetailedBy(key) {
      if (this.detailedSortKey === key) {
        this.detailedSortAsc = !this.detailedSortAsc;
      } else {
        this.detailedSortKey = key;
        this.detailedSortAsc = true;
      }
      this.detailedPage = 1;
    },

    onViewModeChange() {
      if (this.viewMode === 'detailed') this.fetchDetailedDataForAll();
    },

    async fetchDetailedDataForAll() {
      if (!this.date_range || this.date_range.length < 2) {
        this.$bvToast.toast('Please select a date range', { title: 'Warning', variant: 'warning' });
        return;
      }
      this.isLoadingDetailed = true;
      const payload = {
        username: this.packet.userDetails.username,
        dates: this.date_range,
        location_id: null,
        salesperson: null,
        current_user_id: this.packet.userDetails.id,
        userid: this.packet.userDetails.id
      };
      try {
        const res = await axios.post('/api/reports/reports_v5/', payload, { headers: { 'Content-Type': 'application/json' } });
        if (res.data.result && !res.data.error) {
          this.detailedData = res.data.result.map(i => ({ ...i, payment_methods: i.payment_methods || [] }));
          this.detailedPage = 1;
        } else {
          this.detailedData = [];
        }
      } catch (e) {
        console.error(e);
        this.detailedData = [];
      } finally {
        this.isLoadingDetailed = false;
      }
    },

    selectLocationFromTable(locationId) {
      this.selectedLocation = this.selectedLocation === locationId ? null : locationId;
      if (this.selectedLocation !== null) this.selectedSalesperson = null;
      this.fetchSummaryData();
      if (this.viewMode === 'detailed' && (this.selectedLocation || this.selectedSalesperson)) this.fetchDetailedData();
    },

    selectSalespersonFromTable(salesperson) {
      this.selectedSalesperson = this.selectedSalesperson === salesperson ? null : salesperson;
      if (this.selectedSalesperson !== null) this.selectedLocation = null;
      this.fetchSummaryData();
      if (this.viewMode === 'detailed' && (this.selectedLocation || this.selectedSalesperson)) this.fetchDetailedData();
    },

    clearFilters() {
      this.selectedLocation = null;
      this.selectedSalesperson = null;
      this.selectedSaleType = null;
      this.showWarrantyWithDiscount = false;
      this.selectedOrderTypes = [];
      this.selectedDeliveryStatuses = [];
      this.selectedItemFlags = [];
      this.detailedData = [];
      this.fetchSummaryData();
    },

    loadUserData() {
      try {
        this.packet.userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
        const locations = localStorage.getItem('locations');
        if (locations) this.packet.locations = JSON.parse(locations);
        const packet = JSON.parse(localStorage.getItem('packet') || '{}');
        if (packet.locations && packet.locations.length) this.packet.locations = packet.locations;
      } catch (e) {
        console.error('Error loading user data:', e);
      }
    },

    initializeDashboard() {
      const now = moment();
      this.date_range = [
        now.clone().startOf('month').format('MM-DD-YYYY'),
        now.clone().endOf('month').format('MM-DD-YYYY')
      ];
      this.picked_date_range = 'this_month';
      this.fetchSummaryData();
    },

    set_dates(value) {
      let start = null, end = null;
      const now = moment();
      switch (value) {
        case 'today':
          start = end = now.clone().format('MM-DD-YYYY'); break;
        case 'yesterday':
          start = end = now.clone().subtract(1, 'days').format('MM-DD-YYYY'); break;
        case 'this_week':
          start = now.clone().startOf('week').format('MM-DD-YYYY');
          end   = now.clone().endOf('week').format('MM-DD-YYYY'); break;
        case 'last_week':
          start = now.clone().subtract(1, 'week').startOf('week').format('MM-DD-YYYY');
          end   = now.clone().subtract(1, 'week').endOf('week').format('MM-DD-YYYY'); break;
        case 'this_month':
          start = now.clone().startOf('month').format('MM-DD-YYYY');
          end   = now.clone().endOf('month').format('MM-DD-YYYY'); break;
        case 'last_month':
          start = now.clone().subtract(1, 'month').startOf('month').format('MM-DD-YYYY');
          end   = now.clone().subtract(1, 'month').endOf('month').format('MM-DD-YYYY'); break;
      }
      if (start && end) {
        this.date_range = [start, end];
        this.picked_date_range = value;
        this.fetchSummaryData();
      }
    },

    refreshDetailedData() { this.fetchDetailedData(); },

    async fetchSummaryData() {
      if (!this.date_range || this.date_range.length < 2) {
        this.$bvToast.toast('Please select a date range', { title: 'Warning', variant: 'warning' });
        return;
      }
      this.isLoading = true;
      const payload = {
        username: this.packet.userDetails.username,
        dates: this.date_range,
        location_id: this.selectedLocation,
        salesperson: this.selectedSalesperson,
        current_user_id: this.packet.userDetails.id,
        userid: this.packet.userDetails.id
      };
      try {
        const res = await axios.post('/api/reports/reports_optimized/', payload, { headers: { 'Content-Type': 'application/json' } });
        if (res.data.result && !res.data.error) {
          const result = res.data.result;
          this.summaryData        = result.summary || {};
          this.locationSummary    = result.location_summary || {};
          this.salespersonSummary = result.salesperson_summary || {};
          this.dailyTrend         = result.daily_trend || {};
          this.rawSalesData       = result.sales_data || [];
          this.analyzedRange      = [...this.date_range];
          this.gridWeekOffset     = 0;
          this.gridWeekData       = [];
          this.gridWeekReq++;
          this.gridWeekLoading    = false;

          this.isLoading = false;
          this.$nextTick(() => {
            this.$nextTick(() => {
              this.renderDailyTrendChart();
              this.renderLocationChart();
            });
          });
        } else {
          this.isLoading = false;
          this.$bvToast.toast(res.data.error || 'No data returned', { title: 'Info', variant: 'info' });
        }
      } catch (e) {
        this.isLoading = false;
        console.error('API Error:', e);
        this.$bvToast.toast('Error fetching data', { title: 'Error', variant: 'danger' });
      }
    },

    async fetchDetailedData() {
      if (!this.date_range || this.date_range.length < 2) {
        this.$bvToast.toast('Please select a date range', { title: 'Warning', variant: 'warning' });
        return;
      }
      if (!this.selectedLocation && !this.selectedSalesperson) { this.detailedData = []; return; }
      this.isLoadingDetailed = true;
      const payload = {
        username: this.packet.userDetails.username,
        dates: this.date_range,
        location_id: this.selectedLocation,
        salesperson: this.selectedSalesperson,
        current_user_id: this.packet.userDetails.id,
        userid: this.packet.userDetails.id
      };
      try {
        const res = await axios.post('/api/reports/reports_v5/', payload, { headers: { 'Content-Type': 'application/json' } });
        if (res.data.result && !res.data.error) {
          this.detailedData = res.data.result.map(i => ({ ...i, payment_methods: i.payment_methods || [] }));
          this.detailedPage = 1;
        } else {
          this.detailedData = [];
        }
      } catch (e) {
        console.error(e);
        this.detailedData = [];
      } finally {
        this.isLoadingDetailed = false;
      }
    },

    onLocationChange() {
      this.fetchSummaryData();
      if (this.viewMode === 'detailed' && (this.selectedLocation || this.selectedSalesperson)) this.fetchDetailedData();
    },
    onSalespersonChange() {
      this.fetchSummaryData();
      if (this.viewMode === 'detailed' && (this.selectedLocation || this.selectedSalesperson)) this.fetchDetailedData();
    },
    onFilterChange() { this.detailedPage = 1; },

    bucketHourly() {
      const buckets = Array.from({ length: 24 }, (_, h) => ({ hour: h, sales: 0, refunds: 0 }));
      if (!this.rawSalesData || !this.rawSalesData.length) return buckets;
      for (const row of this.rawSalesData) {
        if (!row.ts) continue;
        const h = moment(row.ts).hour();
        const amt = parseFloat(
          row.total_actualized_dollar_amount ||
          row.total_dollar_amount ||
          row.invoice_total || 0
        ) || 0;
        if (row.is_refund || amt < 0) buckets[h].refunds += Math.abs(amt);
        else                          buckets[h].sales   += amt;
      }
      return buckets;
    },

    renderDailyTrendChart(retries = 3) {
      const ctx = this.$refs.dailyTrendChart;
      if (!ctx) {
        if (retries > 0) requestAnimationFrame(() => this.renderDailyTrendChart(retries - 1));
        return;
      }
      if (this.dailyTrendChart) {
        try { this.dailyTrendChart.destroy(); } catch (e) {}
        this.dailyTrendChart = null;
      }

      let labels = [];
      let salesSeries = [];
      let refundsSeries = [];
      let hourlySeries = null;

      if (this.chartMode === 'day') {
        const buckets = this.bucketHourly();
        labels = buckets.map(b => `${String(b.hour).padStart(2, '0')}:00`);
        salesSeries = buckets.map(b => +(b.sales || 0));
        refundsSeries = buckets.map(b => +(b.refunds || 0));
      } else if (this.chartMode === 'week') {
        const rows = (this.rawSalesData || []).filter(r => r.ts);
        if (!rows.length) {
          const keys = Object.keys(this.dailyTrend || {}).sort();
          if (!keys.length) return;
          labels = keys.map(d => moment(d).format('ddd MM-DD'));
          salesSeries = keys.map(d => +(this.dailyTrend[d].sales || 0));
          refundsSeries = keys.map(d => +(this.dailyTrend[d].refunds || 0));
        } else {
          const hourlyMap = {};
          for (const r of rows) {
            const m = moment(r.ts);
            const key = m.format('YYYY-MM-DD HH');
            if (!hourlyMap[key]) hourlyMap[key] = { sales: 0, refunds: 0, date: m.format('YYYY-MM-DD') };
            const amt = parseFloat(
              r.total_actualized_dollar_amount ||
              r.total_dollar_amount ||
              r.invoice_total || 0
            ) || 0;
            if (r.is_refund || amt < 0) hourlyMap[key].refunds += Math.abs(amt);
            else                        hourlyMap[key].sales += amt;
          }
          const sortedKeys = Object.keys(hourlyMap).sort();
          labels = sortedKeys.map(k => moment(k, 'YYYY-MM-DD HH').format('ddd HH:00'));
          hourlySeries = sortedKeys.map(k => hourlyMap[k].sales);
          const dailyByDate = {};
          for (const k of sortedKeys) {
            const d = hourlyMap[k].date;
            if (!dailyByDate[d]) dailyByDate[d] = { sales: 0, refunds: 0, firstKey: k };
            dailyByDate[d].sales   += hourlyMap[k].sales;
            dailyByDate[d].refunds += hourlyMap[k].refunds;
          }
          salesSeries = sortedKeys.map(k => {
            const d = hourlyMap[k].date;
            return k === dailyByDate[d].firstKey ? dailyByDate[d].sales : 0;
          });
          refundsSeries = sortedKeys.map(k => {
            const d = hourlyMap[k].date;
            return k === dailyByDate[d].firstKey ? dailyByDate[d].refunds : 0;
          });
        }
      } else {
        const keys = Object.keys(this.dailyTrend || {}).sort();
        if (!keys.length) return;
        labels = keys.map(d => moment(d).format('MM-DD'));
        salesSeries = keys.map(d => +(this.dailyTrend[d].sales || 0));
        refundsSeries = keys.map(d => +(this.dailyTrend[d].refunds || 0));
      }

      if (!labels.length) return;

      while (labels.length < 2) {
        labels.push(labels[0] || '—');
        salesSeries.push(salesSeries[0] || 0);
        refundsSeries.push(refundsSeries[0] || 0);
        if (hourlySeries) hourlySeries.push(hourlySeries[0] || 0);
      }

      const datasets = [
        {
          label: this.chartMode === 'day' ? 'Hourly Sales'
               : this.chartMode === 'week' ? 'Daily Sales'
               : 'Sales',
          data: salesSeries,
          borderColor: this.chartColors.success,
          backgroundColor: 'rgba(16,185,129,0.12)',
          tension: 0.3,
          fill: true,
          borderWidth: 3,
          pointRadius: 4,
          pointBackgroundColor: this.chartColors.success,
          spanGaps: true
        },
        {
          label: this.chartMode === 'day' ? 'Hourly Refunds' : 'Refunds',
          data: refundsSeries,
          borderColor: this.chartColors.danger,
          backgroundColor: 'rgba(239,68,68,0.06)',
          tension: 0.3,
          fill: true,
          borderWidth: 3,
          borderDash: [5, 5],
          pointRadius: 4,
          pointBackgroundColor: this.chartColors.danger,
          spanGaps: true
        }
      ];

      if (hourlySeries && hourlySeries.some(v => v > 0)) {
        datasets.push({
          label: 'Hourly Sales (actual)',
          data: hourlySeries,
          borderColor: this.chartColors.primary,
          backgroundColor: 'rgba(99,102,241,0.08)',
          tension: 0.35,
          fill: false,
          borderWidth: 3,
          borderDash: [],
          pointRadius: 5,
          pointBackgroundColor: this.chartColors.primary,
          pointBorderColor: '#fff',
          pointBorderWidth: 1.5,
          spanGaps: true
        });
      }

      try {
        this.dailyTrendChart = new Chart(ctx, {
          type: 'line',
          data: { labels, datasets },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              tooltip: {
                callbacks: {
                  label: (c) => {
                    const raw = Number(c.raw) || 0;
                    return `${c.dataset.label}: $${raw.toLocaleString()}`;
                  }
                },
                bodyFont: { size: 15 },
                titleFont: { size: 15 }
              },
              legend: { labels: { font: { size: 14 } } }
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: { callback: v => `$${(+v).toLocaleString()}`, font: { size: 13 } }
              },
              x: {
                ticks: { font: { size: 12 }, maxRotation: 45, autoSkip: true, autoSkipPadding: 10 }
              }
            }
          }
        });
      } catch (err) {
        console.warn('[chart] render failed:', err.message);
        this.dailyTrendChart = null;
      }
    },

    renderLocationChart(retries = 3) {
      const ctx = this.$refs.locationChart;
      if (!ctx) {
        if (retries > 0) requestAnimationFrame(() => this.renderLocationChart(retries - 1));
        return;
      }
      if (this.locationChart) {
        try { this.locationChart.destroy(); } catch (e) {}
        this.locationChart = null;
      }
      if (!this.locationSummary || !Object.keys(this.locationSummary).length) return;

      const locations = Object.keys(this.locationSummary);
      try {
        this.locationChart = new Chart(ctx, {
          type: 'doughnut',
          data: {
            labels: locations.map(l => this.getLocationName(l)),
            datasets: [{
              data: locations.map(l => (this.locationSummary[l].net || 0)),
              backgroundColor: [
                this.chartColors.primary,
                this.chartColors.success,
                this.chartColors.warning,
                this.chartColors.info
              ],
              borderWidth: 0
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { position: 'right', labels: { font: { size: 13 } } },
              tooltip: {
                callbacks: {
                  label: (c) => `${c.label}: $${(+c.raw).toLocaleString()}`
                },
                bodyFont: { size: 15 },
                titleFont: { size: 15 }
              }
            },
            cutout: '60%'
          }
        });
      } catch (err) {
        console.warn('[chart] location render failed:', err.message);
        this.locationChart = null;
      }
    },

    weekdayKey(row) { return moment(row.ts).format('YYYY-MM-DD'); },
    storeKey(row)   { return String(row.location_id); },

    // ◀ ▶ week arrows on the day-and-hour chart (does not change the rest of the report)
    shiftGridWeek(step) {
      if (!step) return;
      this.gridWeekOffset += step;
      if (this.gridWeekOffset === 0) {
        this.gridWeekReq++;
        this.gridWeekData = [];
        this.gridWeekLoading = false;
        return;
      }
      this.fetchGridWeek();
    },

    async fetchGridWeek() {
      if (!this.gridWeekStart) return;
      const start = this.gridWeekStart.clone();
      const end   = start.clone().add(6, 'days');
      const reqId = ++this.gridWeekReq;
      this.gridWeekLoading = true;
      const payload = {
        username: this.packet.userDetails.username,
        dates: [start.format('MM-DD-YYYY'), end.format('MM-DD-YYYY')],
        location_id: this.selectedLocation,
        salesperson: this.selectedSalesperson,
        current_user_id: this.packet.userDetails.id,
        userid: this.packet.userDetails.id
      };
      try {
        const res = await axios.post('/api/reports/reports_optimized/', payload, { headers: { 'Content-Type': 'application/json' } });
        if (reqId !== this.gridWeekReq) return;
        this.gridWeekData = (res.data.result && !res.data.error) ? (res.data.result.sales_data || []) : [];
      } catch (e) {
        if (reqId !== this.gridWeekReq) return;
        console.error('Week chart error:', e);
        this.gridWeekData = [];
        this.$bvToast.toast('Could not load that week', { title: 'Error', variant: 'danger' });
      } finally {
        if (reqId === this.gridWeekReq) this.gridWeekLoading = false;
      }
    },

    exportToCSV() {
      if (this.filteredDetailedData.length === 0) {
        this.$bvToast.toast('No data to export', { title: 'Info', variant: 'info' });
        return;
      }
      const headers = [
        'Date','Sold From','Invoice #','Sold By','Order Type','Delivery Status',
        'Discounted Item','As-Is Item','Order Total','Discount','Disc %',
        'Commission','Extra Charges','Sales Tax','Total Sold',
        'Warranty','Warranty Comm','Delivery','Delivery Comm',
        'Setup','Setup Comm','Spiff','Total Commission'
      ];
      const rows = this.filteredDetailedData.map(i => [
        i.ts, this.getLocationName(i.location_id), i.invoice_id, i.username,
        i.order_type || '', i.delivery_status || '',
        i.has_discount ? 'YES' : 'NO', i.has_as_is ? 'YES' : 'NO',
        (i.order_total || 0).toFixed(2), (i.total_discount || 0).toFixed(2),
        (i.discount_percentage || 0).toFixed(2) + '%',
        (i.adjusted_base_commission || 0).toFixed(2),
        (i.extra_charges || 0).toFixed(2),
        (i.sales_tax || 0).toFixed(2),
        (i.total_dollar_amount || 0).toFixed(2),
        (i.warranty_charges || 0).toFixed(2),
        (i.adjusted_warranty_commission || 0).toFixed(2),
        (i.delivery_charges || 0).toFixed(2),
        (i.adjusted_delivery_commission || 0).toFixed(2),
        (i.setup_charges || 0).toFixed(2),
        (i.adjusted_setup_commission || 0).toFixed(2),
        (i.spiff_commission || 0).toFixed(2),
        (i.adjusted_total_commission || 0).toFixed(2)
      ]);
      const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sales_report_${moment().format('YYYY-MM-DD_HH-mm-ss')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      this.$bvToast.toast(`Exported ${this.filteredDetailedData.length} transactions`,
        { title: 'Success', variant: 'success', autoHideDelay: 1500 });
    },

    handleResize() {
      setTimeout(() => {
        this.renderDailyTrendChart();
        this.renderLocationChart();
      }, 150);
    },

    destroyCharts() {
      ['dailyTrendChart', 'locationChart'].forEach(k => {
        if (this[k]) { try { this[k].destroy(); } catch (e) {} this[k] = null; }
      });
    }
  }
};
</script>

<style scoped>
/* ─── base ───────────────────────────────────────────── */
.dashboard-container {
  position: relative;
  background: #F6F4F0;
  min-height: 100vh;
  padding: 18px 0 60px;
  font-family: 'Manrope', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  color: #221F1C;
  font-size: 16px;
}
.dashboard-container > * {
  max-width: 1800px;
  width: 97%;
  margin-left: auto;
  margin-right: auto;
}
@media (max-width: 767px) {
  .dashboard-container { padding: 10px 0 40px; }
  .dashboard-container > * { width: 100%; padding-left: 12px; padding-right: 12px; }
}

/* ─── hero ───────────────────────────────────────────── */
.dash-hero {
  background: #221F1C;
  color: #fff;
  border-radius: 20px;
  overflow: hidden;
  border-bottom: 5px solid #EE7A22;
  margin-bottom: 14px;
  animation: dashSlideDown 0.5s ease-out;
}
@keyframes dashSlideDown {
  from { opacity: 0; transform: translateY(-12px); }
  to   { opacity: 1; transform: translateY(0); }
}
.dash-hero-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.8fr) minmax(0, 1fr);
  gap: 24px;
  padding: 22px 30px;
  align-items: center;
}
.dash-hero-title {
  font-family: 'Sora', system-ui, sans-serif;
  font-size: 28px;
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: 0.02em;
}
.dash-hero-sub {
  font-size: 15px;
  color: rgba(255, 255, 255, 0.65);
  margin-top: 6px;
}
.dash-hero-side { display: flex; justify-content: flex-end; }
.dash-hero-qr {
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 16px;
  padding: 12px 20px;
}
.dash-qr-title { font-family: 'Sora', sans-serif; font-weight: 700; font-size: 16px; color: #fff; }
.dash-qr-sub { font-size: 13.5px; color: rgba(255, 255, 255, 0.6); margin-top: 2px; }
@media (max-width: 900px) {
  .dash-hero-grid { grid-template-columns: 1fr; gap: 14px; padding: 18px 20px; }
  .dash-hero-title { font-size: 22px; }
  .dash-hero-side { justify-content: flex-start; }
}

/* ─── cards ──────────────────────────────────────────── */
.dash-card {
  background: #fff;
  border: 1px solid #E5E1DA;
  border-radius: 16px;
  padding: 16px 20px;
  margin-bottom: 12px;
  animation: dashFadeUp 0.4s ease-out both;
}
@keyframes dashFadeUp {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ─── filters ────────────────────────────────────────── */
.dash-filtercard { padding: 16px 20px; }
.dash-filters-row { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.dash-fb-dates { flex: 0 1 380px; min-width: 280px; }

.dash-inputgroup { display: flex; flex-wrap: nowrap; width: 100%; }
.dash-inputgroup-prepend {
  display: inline-flex; align-items: center; justify-content: center;
  height: 48px; padding: 0 14px;
  background: #F1EEE9;
  border: 1.5px solid #E5E1DA; border-right: 0;
  border-radius: 10px 0 0 10px;
  color: #736C63; font-size: 17px;
}
.dash-inputgroup >>> .dash-date-input {
  height: 48px !important;
  border: 1.5px solid #E5E1DA !important;
  border-radius: 0 10px 10px 0 !important;
  background: #F7F5F2 !important;
  padding: 0 14px !important;
  font-size: 15px !important;
  font-weight: 600 !important;
  color: #221F1C !important;
}
.dash-inputgroup >>> .dash-date-input:focus {
  border-color: #EE7A22 !important; background: #fff !important; box-shadow: none !important;
}

.dash-quickdates { display: flex; gap: 6px; flex-wrap: wrap; }
.dash-quick-btn {
  background: #F7F5F2;
  border: 1.5px solid #E5E1DA;
  border-radius: 999px;
  padding: 9px 15px;
  font-size: 13.5px;
  font-weight: 700;
  color: #4A4540; cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  display: inline-flex; align-items: center; gap: 6px;
}
.dash-quick-btn:hover { border-color: #EE7A22; color: #D4620F; }
.dash-quick-btn.is-active { background: #EE7A22; border-color: #EE7A22; color: #fff; }

.dash-search-btn { margin-left: auto; }
.dash-btn-primary {
  background: #EE7A22; border: 1.5px solid #EE7A22; color: #fff;
  border-radius: 10px; font-weight: 700; font-size: 15px;
  padding: 11px 22px;
  display: inline-flex; align-items: center; gap: 8px; cursor: pointer;
  transition: all 0.15s ease;
}
.dash-btn-primary:hover:not(:disabled) { background: #D4620F; border-color: #D4620F; transform: translateY(-1px); }
.dash-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

.dash-btn-ghost {
  background: #fff; border: 1.5px solid #E5E1DA; color: #221F1C;
  border-radius: 10px; font-weight: 700; font-size: 13.5px;
  padding: 8px 14px;
  display: inline-flex; align-items: center; gap: 6px;
  cursor: pointer; transition: all 0.15s ease;
}
.dash-btn-ghost:hover { border-color: #EE7A22; color: #D4620F; }

.dash-linkbtn {
  background: none; border: 0; color: #D4620F;
  font-weight: 700; font-size: 14px; padding: 0;
  display: inline-flex; align-items: center; gap: 6px; cursor: pointer;
}
.dash-linkbtn:hover { color: #EE7A22; text-decoration: underline; }

.dash-advanced-toggle-wrap { margin-top: 12px; }

.dash-advanced-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-top: 14px; padding-top: 14px;
  border-top: 1px dashed #E5E1DA;
  animation: dashFadeUp 0.35s ease-out;
}
@media (max-width: 992px) { .dash-advanced-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 576px) { .dash-advanced-grid { grid-template-columns: 1fr; } }

.dash-adv-item { display: flex; flex-direction: column; gap: 8px; }
.dash-label {
  font-size: 12.5px; font-weight: 800; letter-spacing: 0.08em; color: #736C63;
  text-transform: uppercase; display: inline-flex; align-items: center; gap: 6px;
}
.dash-select {
  height: 48px !important; border-radius: 10px !important;
  border: 1.5px solid #E5E1DA !important; background: #F7F5F2 !important;
  font-size: 15px !important; font-weight: 600 !important; color: #221F1C !important;
}
.dash-select:focus { border-color: #EE7A22 !important; box-shadow: none !important; }

.dash-radio-group >>> .btn { font-size: 14px; font-weight: 700; padding: 8px 16px; }

/* ─── KPI cards ──────────────────────────────────────── */
.dash-kpi-grid {
  display: grid; grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 12px; margin-bottom: 14px;
}
.dash-kpi {
  display: flex; align-items: center; gap: 14px;
  background: #fff; border: 1px solid #E5E1DA;
  border-left: 6px solid transparent;
  border-radius: 14px; padding: 14px 16px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.dash-kpi:hover { transform: translateY(-2px); box-shadow: 0 12px 24px -16px rgba(34, 31, 28, 0.4); }
.dash-kpi-icon {
  width: 50px; height: 50px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 23px; flex-shrink: 0;
}
.dash-kpi-body { display: flex; flex-direction: column; min-width: 0; }
.dash-kpi-value {
  font-family: 'Sora', sans-serif;
  font-size: 24px;
  font-weight: 800; line-height: 1.1; letter-spacing: -0.01em;
  color: #221F1C;
}
.dash-kpi-label { font-size: 14px; color: #736C63; font-weight: 600; margin-top: 3px; }
.dash-kpi-trend { font-size: 12.5px; font-weight: 700; color: #736C63; margin-top: 4px; }
.dash-kpi-trend.is-good { color: #0F6E6B; }
.dash-kpi-trend.is-bad  { color: #C93C30; }

.dash-kpi.is-total      { border-left-color: #D4620F; }
.dash-kpi.is-total      .dash-kpi-icon  { background: linear-gradient(135deg, #EE7A22, #D4620F); }
.dash-kpi.is-total      .dash-kpi-value { color: #D4620F; }

.dash-kpi.is-regular    { border-left-color: #3F8AC9; }
.dash-kpi.is-regular    .dash-kpi-icon  { background: linear-gradient(135deg, #3F8AC9, #2D6A9D); }
.dash-kpi.is-regular    .dash-kpi-value { color: #2D6A9D; }

.dash-kpi.is-net        { border-left-color: #8A5CC7; }
.dash-kpi.is-net        .dash-kpi-icon  { background: linear-gradient(135deg, #8A5CC7, #6A3FA5); }
.dash-kpi.is-net        .dash-kpi-value { color: #6A3FA5; }

.dash-kpi.is-layaway    { border-left-color: #C98A12; }
.dash-kpi.is-layaway    .dash-kpi-icon  { background: linear-gradient(135deg, #E0A524, #C98A12); }
.dash-kpi.is-layaway    .dash-kpi-value { color: #C98A12; }

.dash-kpi.is-refund     { border-left-color: #C93C30; }
.dash-kpi.is-refund     .dash-kpi-icon  { background: linear-gradient(135deg, #C93C30, #A22A1F); }
.dash-kpi.is-refund     .dash-kpi-value { color: #C93C30; }

.dash-kpi.is-commission { border-left-color: #0F6E6B; }
.dash-kpi.is-commission .dash-kpi-icon  { background: linear-gradient(135deg, #0F6E6B, #0B5552); }
.dash-kpi.is-commission .dash-kpi-value { color: #0F6E6B; }

@media (max-width: 1600px) { .dash-kpi-grid { grid-template-columns: repeat(3, 1fr); } }
@media (max-width: 991px)  { .dash-kpi-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 480px)  { .dash-kpi-grid { grid-template-columns: 1fr; } }

/* ─── charts ─────────────────────────────────────────── */
.dash-charts-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
  gap: 12px; margin-bottom: 12px;
}
@media (max-width: 992px) { .dash-charts-grid { grid-template-columns: 1fr; } }

.dash-chart-card { padding: 0; overflow: hidden; }
.dash-chart-card--wide { grid-column: 1 / -1; }
.dash-chart-body { padding: 16px 18px; height: 360px; position: relative; }
.dash-chart-body--tall { height: 620px; }

.dash-chart-legend {
  display: flex; gap: 14px; align-items: center;
  font-size: 12.5px; color: #736C63; font-weight: 700;
}
.dash-legend-item { display: inline-flex; align-items: center; gap: 6px; }
.dash-legend-swatch {
  width: 18px; height: 0; border-top: 3px solid #0F6E6B; display: inline-block;
}
.dash-legend-swatch.is-solid  { border-top-style: solid; border-top-color: #10b981; }
.dash-legend-swatch.is-dashed { border-top-style: dashed; border-top-color: #6366f1; border-top-width: 2.5px; }
.dash-legend-swatch.is-bar-sales  {
  width: 14px; height: 10px; border-radius: 3px; border: 0;
  background: rgba(16,185,129,0.85);
}
.dash-legend-swatch.is-bar-refund {
  width: 14px; height: 10px; border-radius: 3px; border: 0;
  background: rgba(239,68,68,0.75);
}

/* ─── collapsible chart headers (time grids) ─────────── */
.dash-collapse-header {
  width: 100%;
  background: #fff;
  border: 0;
  border-bottom: 1px solid transparent;
  text-align: left;
  cursor: pointer;
  color: #221F1C;
  transition: background 0.15s ease;
}
.dash-collapse-header.is-open { border-bottom-color: #F1EEE9; }
.dash-collapse-header:hover { background: #FFF8F0; }
.dash-collapse-header:focus-visible { outline: 2px solid #EE7A22; outline-offset: -2px; }
.dash-collapse-action {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 13.5px; font-weight: 700; color: #D4620F;
  padding: 6px 12px; border-radius: 999px;
  background: #FFF1E4; border: 1px solid #F5CFAE;
}
.dash-collapse-chevron { transition: transform 0.2s ease; }
.dash-collapse-header.is-open .dash-collapse-chevron { transform: rotate(180deg); }
@media (prefers-reduced-motion: reduce) { .dash-collapse-chevron { transition: none; } }

/* ─── card headers ───────────────────────────────────── */
.dash-card-header {
  display: flex; justify-content: space-between; align-items: center;
  gap: 10px; flex-wrap: wrap;
  padding: 14px 18px; border-bottom: 1px solid #F1EEE9;
}
.dash-card-title {
  font-family: 'Sora', sans-serif;
  font-size: 17px;
  font-weight: 700;
  display: flex; align-items: center; gap: 10px; color: #221F1C;
}
.dash-table-badge {
  font-size: 12px; font-weight: 800;
  padding: 4px 10px; border-radius: 999px;
  background: #F1EEE9; color: #4A4540;
  border: 1px solid #E5E1DA;
  letter-spacing: 0.02em;
  display: inline-flex; align-items: center; gap: 4px;
}

/* ─── notice bar ─────────────────────────────────────── */
.dash-noticebar {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; background: #FFF1E4; border: 1px solid #F5CFAE;
  border-radius: 12px; padding: 10px 16px; margin-bottom: 12px;
  flex-wrap: wrap;
}
.dash-chip {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 13.5px; font-weight: 700; padding: 5px 12px;
  border-radius: 999px; background: #F1EEE9; color: #4A4540;
  border: 1px solid #E5E1DA;
}
.dash-chip.is-orange { background: #FFF1E4; color: #D4620F; border-color: #F5CFAE; }

/* ─── duplicate view-mode row ────────────────────────── */
.dash-viewmode-row {
  display: flex; justify-content: flex-end;
  margin: 4px 0 12px;
}

/* ─── SUMMARY tables grid ───────────────────────────── */
.dash-tables-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px; margin-bottom: 12px;
}
@media (max-width: 1100px) { .dash-tables-grid { grid-template-columns: 1fr; } }

.dash-tablecard { padding: 0; overflow: hidden; }
.dash-table-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
.dash-table-scroll--fixed { overflow-x: hidden; }

.dash-table {
  width: 100%; border-collapse: separate; border-spacing: 0;
  font-size: 14px;
}
.dash-table { min-width: 720px; }
.dash-table--sp { min-width: 0; table-layout: fixed; }

.dash-table th {
  position: sticky; top: 0; z-index: 2;
  background: #221F1C; color: #fff;
  text-align: left;
  font-family: 'Sora', sans-serif;
  font-size: 11.5px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 13px 12px;
  white-space: nowrap;
  border-bottom: 3px solid #EE7A22;
}
.dash-table th.is-right { text-align: right; }

.dash-row td {
  padding: 12px 12px;
  border-bottom: 1px solid #F1EEE9;
  vertical-align: middle;
  background: #fff; color: #221F1C;
  font-size: 14px;
}

.dash-row { transition: background 0.15s ease; }
.dash-row:hover td { background: #FFF8F0; }
.dash-row.is-clickable { cursor: pointer; }
.dash-row.is-clickable:hover td { background: #FFF8F0; }
.dash-row.is-highlight td { background: #FFF1E4 !important; box-shadow: inset 3px 0 0 #EE7A22; }

.dash-table td.is-right { text-align: right; }

.dash-loc-cell {
  display: inline-flex; align-items: center; gap: 8px;
  font-weight: 700; font-size: 14px;
}
.dash-loc-icon { color: #EE7A22; font-size: 13px; flex-shrink: 0; }

.dash-cell-name,
.dash-sp-name {
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  max-width: 100%;
}

.dash-num {
  font-family: 'Sora', sans-serif;
  font-weight: 700;
  font-size: 19px;
  letter-spacing: -0.01em;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.dash-num.is-danger { color: #C93C30; }
.dash-num.is-teal   { color: #0F6E6B; }

.dash-totals-row td,
.dash-table tfoot td {
  background: #221F1C !important;
  color: #fff !important;
  border-top: 0; border-bottom: 0;
  padding: 13px 12px;
  font-weight: 800;
}
.dash-totals-label {
  font-family: 'Sora', sans-serif;
  font-size: 12px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #FFB06B;
}
.dash-totals-amt {
  font-family: 'Sora', sans-serif;
  font-size: 14px;
  font-weight: 800;
  color: #FFB06B;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

/* ─── detailed table ─────────────────────────────────── */
.dash-detailcard { padding: 0; overflow: hidden; }
.dash-detail-actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
.dash-dd >>> .btn { font-size: 13px; font-weight: 700; border-radius: 8px; padding: 6px 12px; }

.compact-detail-wrapper {
  width: 100%;
  overflow-x: hidden;
}
.compact-sales-table {
  width: 100%;
  table-layout: fixed;
  font-size: 13px;
  border-collapse: collapse;
}
.compact-sales-table th,
.compact-sales-table td {
  padding: 6px 4px;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
  border: 1px solid #dee2e6;
}
.compact-sales-table th {
  background: #c3cdd3;
  font-weight: 700;
  position: sticky;
  top: 0;
  font-size: 11px;
  letter-spacing: 0.2px;
  padding: 8px 3px;
  line-height: 1.15;
  text-transform: uppercase;
  cursor: pointer;
}
.compact-sales-table td.text-right { text-align: right; }
.compact-sales-table th.text-right { text-align: right; }

.compact-sales-table .col-date      { width: 6%; }
.compact-sales-table .col-loc       { width: 7%; }
.compact-sales-table .col-inv       { width: 6.5%; }
.compact-sales-table .col-by        { width: 5%; }
.compact-sales-table .col-ordertype { width: 5.5%; }
.compact-sales-table .col-status    { width: 4.5%; }
.compact-sales-table .col-num       { width: 4%; }
.compact-sales-table .col-num-sm    { width: 3.5%; }

.compact-detail-wrapper ::-webkit-scrollbar { display: none; }

.compact-sales-table .header {
  font-weight: bold;
  background-color: lightyellow;
  font-size: 12px;
}
.compact-sales-table .non-header {
  font-weight: normal;
  font-size: 12px;
}

.status-badge {
  font-size: 10px;
  padding: 3px 6px;
  letter-spacing: 0.3px;
}
.flag-badge {
  font-size: 9px;
  padding: 1px 5px;
  font-weight: 700;
  vertical-align: middle;
}

.invoice-link {
  color: #2563eb;
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
  transition: color 0.2s ease, background-color 0.2s ease;
  padding: 1px 3px;
  border-radius: 3px;
  display: inline-block;
}
.invoice-link:hover {
  color: #fff;
  background-color: #2563eb;
  text-decoration: none;
}
.compact-sales-table .invoice-link { font-size: 12px; }

.pagination-wrapper {
  border-top: 1px solid #e2e8f0;
  padding: 16px;
}

/* ─── loading / empty / hints ────────────────────────── */
.dash-loading-inline {
  display: flex; align-items: center; justify-content: center;
  gap: 12px; color: #736C63; font-weight: 600; font-size: 16px;
  padding: 40px 20px;
}
.dash-empty {
  text-align: center; padding: 60px 30px;
  border: 2px dashed #D6CFC4; background: #FAF9F7;
}
.dash-empty-icon {
  width: 78px; height: 78px; border-radius: 22px;
  background: #FFF1E4; color: #EE7A22;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 34px; margin-bottom: 18px;
}
.dash-empty h3 { font-family: 'Sora', sans-serif; font-size: 23px; font-weight: 700; margin: 0 0 8px; }
.dash-empty p { color: #736C63; max-width: 50ch; margin: 0 auto; line-height: 1.6; font-size: 16px; }

.dash-hint {
  display: flex; align-items: center; gap: 8px; justify-content: center;
  text-align: center; padding: 16px;
  background: #FAF9F7; border: 1px dashed #E5E1DA;
  border-radius: 12px; color: #736C63;
  font-size: 14.5px; margin-top: 12px;
}
.dash-hint.is-info { background: #E2F2F0; border-color: #BFE1DD; color: #0F6E6B; }

/* ─── transitions ────────────────────────────────────── */
.dash-fade-enter-active, .dash-fade-leave-active { transition: opacity 0.25s ease; }
.dash-fade-enter, .dash-fade-leave-to { opacity: 0; }

@media (max-width: 991px) {
  .dash-table th,
  .dash-table td,
  .dash-totals-row td { padding: 10px 10px; font-size: 13px; }
  .dash-num { font-size: 13px; }
  .dash-totals-amt { font-size: 13px; }
  .dash-table th { font-size: 10.5px; }
  .compact-sales-table th,
  .compact-sales-table td { font-size: 10px; padding: 5px 2px; }
  .compact-sales-table th { font-size: 9px; }
}

@media (prefers-reduced-motion: reduce) {
  .dash-hero, .dash-card, .dash-row { animation: none !important; }
}
</style>


// WEBPACK FOOTER //
// src/components/Reports/ReportsMySales.vue